import { snakecaseKeys } from '@src/util/functions'
import { convertToYYYYMMDD } from '@src/util/date-format'
import camelcaseKeys from 'camelcase-keys'
import { ExpertiseLevel } from '@src/data/member'

export enum AffectedProduct {
  STATION_3D = 'station_3d',
  STATION_2D = 'station_2d',
  PLATFORM = 'platform',
  PLATFORM_OLD = 'platform_old',
  IOS = 'ios',
  ANDROID = 'android',
  STATION_LEGACY = 'station_legacy',
  STATION_SOFTWARE = 'station_software'
}

export enum StationIssueType {
  STATION_SOFTWARE = 'station_software',
  STATION_HARDWARE = 'station_hardware'
}

export class SupportTicket {
  affectedProduct: AffectedProduct | null = null
  stationIssueType: StationIssueType | null = null

  date: string = ''
  attachment: any = null

  // contact email to reach the reporter
  email: string = ''

  memberId: number | null = null
  description: string = ''

  howToReproduce: string = ''

  browser: string = ''
  reproducibleInAnotherBrowser: string = ''

  device: string = ''
  appVersion: string = ''

  stationId: number | null = null
  affectedStations: string = ''
  attemptsToFix: string = ''

  exerciseId: number | null = null

  constructor (attributes?) {
    Object.assign(this, attributes)
  }

  formatTicket () {
    return {
      supportTicket: {
        problem_type: this.affectedProduct,
        date: convertToYYYYMMDD(this.date),

        ...this.getAttachment(),

        ...this.getPlatformTicketDetails(),
        ...this.getMobileTicketDetails(),
        ...this.getStationHardwareDetails(),
        ...this.getStationSoftwareDetails()
      }
    }
  }

  private getAttachment () {
    if (this.attachment) {
      return {
        attachment: {
          content: this.attachment.content.split(',')[1],
          filename: this.attachment.filename,
          content_type: this.attachment.content_type
        }
      }
    }
  }

  private getPlatformTicketDetails () {
    if (this.affectedProduct === AffectedProduct.PLATFORM || this.affectedProduct === AffectedProduct.PLATFORM_OLD) {
      return {
        details: snakecaseKeys({
          email: this.email,
          browser: this.browser,
          memberId: this.memberId,
          description: this.description,
          howToReproduce: this.howToReproduce,
          reproducibleInAnotherBrowser: this.reproducibleInAnotherBrowser
        })
      }
    }
  }

  private getMobileTicketDetails () {
    if (this.affectedProduct === AffectedProduct.ANDROID || this.affectedProduct === AffectedProduct.IOS) {
      return {
        details: snakecaseKeys({
          email: this.email,
          device: this.device,
          appVersion: this.appVersion,
          memberId: this.memberId,
          description: this.description
        })
      }
    }
  }

  private getStationHardwareDetails () {
    if (this.stationIssueType === StationIssueType.STATION_HARDWARE) {
      return {
        problem_type: StationIssueType.STATION_HARDWARE,
        details: snakecaseKeys({
          email: this.email,
          stationId: this.stationId,
          description: this.description,
          affectedStations: this.affectedStations,
          attemptsToFix: this.attemptsToFix
        })
      }
    }
  }

  private getStationSoftwareDetails () {
    if (this.stationIssueType === StationIssueType.STATION_SOFTWARE) {
      return {
        problem_type: StationIssueType.STATION_SOFTWARE,
        details: snakecaseKeys({
          email: this.email,
          stationId: this.stationId,
          exerciseId: this.exerciseId,
          description: this.description
        })
      }
    }
  }
}

export enum StateRecording {
  NOT_STARTED = 'not_started',
  STARTED = 'started',
  FINISHED_AND_UPLOADING = 'finished_and_uploading',
  FINISHED_AND_UPLOADED = 'finished_and_uploaded'
}

export enum StateTicket {
  DRAFT = 'draft',
  OPEN = 'open',
  CLOSED = 'closed',
  IN_PROGRESS = 'in_progress'
}

export class ExerciseSupportTicketDetails {
  exerciseId: number | null
  stationId: number | null
  description: string
  stateRecording: StateRecording

  constructor (attributes?) {
    if (attributes) {
      this.description = attributes.notes || attributes.description || ''
      this.exerciseId = attributes.exerciseId
      this.stationId = attributes.stationId
      this.stateRecording = attributes.stateRecording
    } else {
      this.description = ''
      this.exerciseId = null
      this.stationId = null
      this.stateRecording = StateRecording.NOT_STARTED
    }
  }
}

export class ExerciseSupportTicketDetailsDTO {
  exerciseId: number
  description: string
  stationId: number

  constructor (attributes?) {
    if (attributes) {
      this.description = attributes.description
      this.exerciseId = attributes.exerciseId
      this.stationId = attributes.stationId
    }
  }
}

export class Comment {
  id: number
  body: string
  authorType: string
  createdAt: string

  constructor (attributes?) {
    if (attributes) {
      Object.assign(this, attributes)
    }
  }
}

export class ExerciseSupportTicket {
  date: string
  details: ExerciseSupportTicketDetails
  id: number | null
  problemType: AffectedProduct
  feedbackLevel: ExpertiseLevel
  video: string
  state: StateTicket
  comments: Comment[]

  constructor (attributes?) {
    if (attributes) {
      Object.assign(this, attributes)
      this.details = new ExerciseSupportTicketDetails(camelcaseKeys(attributes.details))
      this.comments = attributes.comments ? attributes.comments.map((comment) => new Comment(camelcaseKeys(comment))) : []
    } else {
      this.date = convertToYYYYMMDD(new Date())
      this.id = null
      this.problemType = AffectedProduct.STATION_LEGACY
      this.video = ''
      this.state = StateTicket.DRAFT
      this.details = new ExerciseSupportTicketDetails()
      this.feedbackLevel = ExpertiseLevel.BEGINNER
      this.comments = []
    }
  }
}

export class ExerciseSupportTicketDTO {
  date: string
  details: ExerciseSupportTicketDetailsDTO
  problemType: AffectedProduct
  feedbackLevel: ExpertiseLevel

  constructor (attributes?) {
    this.problemType = attributes.problemType
    this.feedbackLevel = attributes.feedbackLevel
    this.date = attributes.date
    this.details = new ExerciseSupportTicketDetailsDTO((attributes.details))
  }
}

export class CommentParameters {
  commentBody: string
  commentId: number
  constructor (attributes?) {
    if (attributes) {
      Object.assign(this, attributes)
    }
  }
}

export class StateParameters {
  from: StateTicket
  to: StateTicket
  constructor (attributes?) {
    if (attributes) {
      Object.assign(this, attributes)
    }
  }
}

export enum NotificationType {
  STATE_CHANGED = 'support_ticket.state_changed',
  COMMENT_ADDED = 'support_ticket.comment_added'
}

export class ExerciseSupportTicketNotification {
  id: number
  key: NotificationType
  unread: boolean
  parameters: CommentParameters | StateParameters
  initiatorType: string
  createdAt: string
  supportTicket: ExerciseSupportTicket

  constructor (attributes?) {
    if (attributes) {
      Object.assign(this, attributes)
      this.supportTicket = new ExerciseSupportTicket(camelcaseKeys(attributes.supportTicket))
      this.parameters = new CommentParameters(camelcaseKeys(attributes.parameters))
    }
  }
}
